import { GroupRoles } from '@bp/pim-auth-constants';
import { graphql } from 'client/gql';
import type { MatrixEvent } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { mapNotification } from '../../../components/Header/components/Notifications/mapNotification';
import { useMatrixStatus } from '../../../matrix/hooks/useMatrixStatus';
import { useRooms } from '../../../matrix/hooks/useRooms';
import { isNotificationType, NotificationReason, type NotificationType } from '../../../utils/matrixClient';
import { useAuthClaims } from '../../useAuthClaims';
import { useMatrixClient } from '../useMatrixClient';

export enum BpRoomType {
  DM = 'dm',
  GROUP = 'group',
  COURSE = 'course',
  ORGANIZATION = 'organization',
  SUBMISSION = 'submission',
}

const NotificationsQueryDocument = graphql(`
  query NotificationsData(
    $submissionWhere: SubmissionWhere!
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $organizationUuid: ID!
  ) {
    submissions(where: $submissionWhere) {
      uuid
      __typename
      owner {
        uuid
        __typename
      }
    }
    groups(where: $groupWhere) {
      uuid
      __typename
      name
      isCommunity
    }
    calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: $calendarEventWhere) {
      uuid
      __typename
      holderConnection {
        edges {
          node {
            __typename
            ... on Organization {
              uuid
            }
            ... on Group {
              uuid
            }
            ... on Profile {
              uuid
            }
          }
        }
      }
    }
    collaboratingOrganizations(uuid: $organizationUuid) {
      uuid
      name
    }
  }
`);

export const useNotifications = () => {
  const { pimAuthClaims } = useAuthClaims();

  const { isReady } = useMatrixStatus();
  const online = isReady;

  const unfilteredRooms = useRooms();

  const rooms = unfilteredRooms?.filter((room) => {
    const parts = room?.name.split(':')[0].split('_');
    const roomType: BpRoomType = parts[0] as BpRoomType;
    return roomType !== BpRoomType.DM;
  });

  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const otherProfilesUuids = pimAuthClaims.getOtherProfiles().map((p) => p.uuid);

  const [{ data }, refetchSubmissions] = useQuery({
    query: NotificationsQueryDocument,
    variables: {
      submissionWhere: {
        ownerConnection: {
          node: {
            uuid_IN: [profileUuid, ...otherProfilesUuids],
          },
        },
      },
      groupWhere: {
        OR: [
          { editors_SOME: { uuid_IN: [profileUuid, ...otherProfilesUuids] } },
          { viewers_SOME: { uuid_IN: [profileUuid, ...otherProfilesUuids] } },
        ],
      },
      calendarEventWhere: {
        attendees_SOME: { uuid: profileUuid },
      },
      organizationUuid,
    },
  });

  const profileSubmissions = data?.submissions;
  const groupsData = data?.groups;
  const calendarEventsData = data?.calendarEvents;
  const collaboratingOrganizations = data?.collaboratingOrganizations;

  function dontShow(matrixEvent: MatrixEvent, type: string, latestRoomJoinEvent: number) {
    return (
      // hide legacy messages
      !Object.values(NotificationReason).includes(type as NotificationReason) ||
      // hide certain messages from the past
      (latestRoomJoinEvent > matrixEvent.getTs() &&
        type !== NotificationReason.GroupAnnouncement &&
        type !== NotificationReason.CourseAnnouncement &&
        type !== NotificationReason.OrganizationAnnouncement &&
        type !== NotificationReason.GroupMaterialCreated &&
        type !== NotificationReason.CourseMaterialCreated)
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: seems to be necessary to ignore some of them
  useEffect(() => {
    const notifications: NotificationType[] = [];

    const latestJoinEventByRoom: Record<string, number> = {};

    const _updateLatestJoinEventTS = (matrixEvent: MatrixEvent) => {
      const isJoinEvent = matrixEvent.getContent().membership === 'join';
      const eventTs = matrixEvent.getTs();
      const roomId = matrixEvent.getRoomId();

      if (isJoinEvent && roomId) {
        if (roomId in latestJoinEventByRoom) {
          // we have apparently already seen a 'join' event for this user in this room; take the latest
          const oldEventTs = latestJoinEventByRoom[roomId];

          if (eventTs > oldEventTs) {
            latestJoinEventByRoom[roomId] = eventTs;
          }
        } else {
          latestJoinEventByRoom[roomId] = eventTs;
        }
      }
    };

    rooms?.forEach((room) => {
      const events = room?.getLiveTimeline().getEvents();
      const parts = room?.name.split(':')[0].split('_');
      const subjectUuid = parts[1];
      const roomType = parts[0] as BpRoomType;
      const groupUuid: string | undefined = parts[2];

      // baseData based on room Infos
      const baseData: Partial<NotificationType> = {};

      switch (roomType) {
        case BpRoomType.GROUP: {
          const group = groupsData?.find((g) => g?.uuid === groupUuid);
          if (!group?.uuid) return;
          if (group) {
            baseData.subjectName = group.name;
            baseData.groupUuid = group.uuid;
            baseData.title = group.name;
            baseData.isCommunity = group.isCommunity ?? false;
          }
          break;
        }

        case BpRoomType.ORGANIZATION: {
          const collabOrga = collaboratingOrganizations?.find((o) => o.uuid === subjectUuid);
          if (collabOrga) {
            // other organisation
            baseData.subjectUuid = subjectUuid;
            baseData.subjectName = collabOrga.name ?? '';
          } else {
            // this organization
            baseData.subjectUuid = pimAuthClaims.getOrganizationUuid();
            baseData.subjectName = pimAuthClaims.getOrganization().name ?? '';
          }
          break;
        }
      }

      for (const matrixEvent of events) {
        _updateLatestJoinEventTS(matrixEvent);

        const eventBody = matrixEvent.getContent().body;
        if (!eventBody || roomType === BpRoomType.SUBMISSION) continue;

        const roomId = matrixEvent.getRoomId();

        let payload: NotificationType | null = null;
        try {
          payload = JSON.parse(eventBody);
        } catch {
          // probably is a text message
          // the only place we use this are submissions
          if (
            eventBody &&
            // a teacher has no submissions, and only he / her should see this message
            !profileSubmissions?.find((s) => s.owner.uuid === pimAuthClaims.getProfile().uuid)
          ) {
            refetchSubmissions({ where: { uuid: groupUuid } });

            payload = {
              type: NotificationReason.SubmissionComment,
              role: GroupRoles.Editor,
              date: matrixEvent.getDate() ?? new Date(),
              eventId: matrixEvent.getId(),
              roomId,
              content: eventBody,
            };
          }
        }
        if (payload && isNotificationType(payload)) {
          // can be removed when typing ist consistent
          const type = (payload.type?.charAt(0).toLowerCase() + payload?.type?.slice(1)) as NotificationReason;

          if (roomId && dontShow(matrixEvent, type, latestJoinEventByRoom[roomId])) continue;

          const data: NotificationType = {
            date: matrixEvent.getDate() ?? new Date(),
            type: payload.type,
            eventId: matrixEvent.getId(),
            roomId: matrixEvent.getRoomId(),
          };

          // TODO: handle organization- and 1v1-meetings
          if (
            [
              NotificationReason.GroupEventStarted,
              NotificationReason.CourseEventStarted,
              // NotificationReason.OrganizationEventStarted,
              NotificationReason.GroupEventEnded,
              NotificationReason.CourseEventEnded,
              // NotificationReason.OrganizationEventEnded,
            ].includes(type)
          ) {
            const event = calendarEventsData?.find((evt) => evt.uuid === payload?.subjectUuid);
            payload.groupUuid =
              event?.holderConnection.edges
                ?.filter((edge) => edge.node.__typename === 'Group')
                .map((edge) => edge.node.uuid)
                .shift() ?? '';
          }

          const { content, targetUrl } = mapNotification({
            ...baseData,
            ...payload,
            organizationUuid,
          });
          if (content === '') continue;

          if (content) {
            data.content = content;
            data.targetUrl = targetUrl;
            notifications.push(data);
          }
        }
      }
    });
    const sorted = notifications.sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
    setNotifications(sorted);
  }, [online, organizationUuid, groupsData, profileSubmissions, collaboratingOrganizations]);
  return notifications;
};
